import type { CloseReview } from "@/lib/api";
import { monthsBetween } from "./cultivation-dates";
import type { CultivationImport, NoActivityAttestation } from "./cultivation-types";

export const REQUIRED_PROFILES = [
  { id: "plant_inventory", label: "Plant inventory" },
  { id: "harvest", label: "Harvest" },
  { id: "plants_destroyed", label: "Plants destroyed" },
  { id: "transfers", label: "Transfers" },
] as const;

export const OPTIONAL_PROFILES = [
  { id: "wholesale_transfers", label: "Wholesale transfers" },
  { id: "package_inventory", label: "Package inventory" },
] as const;

const PROFILE_LABELS = new Map<string, string>(
  [...REQUIRED_PROFILES, ...OPTIONAL_PROFILES].map((profile) => [profile.id, profile.label]),
);

export function profileLabel(profile: string): string {
  return PROFILE_LABELS.get(profile) ?? profile.replaceAll("_", " ");
}

export function profileIsRequired(profile: string): boolean {
  return REQUIRED_PROFILES.some((required) => required.id === profile);
}

export type EvidenceCellStatus = "ready" | "no_activity" | "missing" | "optional" | "stale";

export interface EvidenceCell {
  profile: string;
  month: string;
  status: EvidenceCellStatus;
  source: CultivationImport | null;
  attestation: NoActivityAttestation | null;
}

export interface EvidenceCoverage {
  months: string[];
  cells: EvidenceCell[];
  missing: EvidenceCell[];
  stale: CultivationImport[];
  ready: boolean;
}

export function evidenceCoverage(
  imports: readonly CultivationImport[],
  licenseId: string,
  periodStart: string,
  periodEnd: string,
  attestations: readonly NoActivityAttestation[] = [],
): EvidenceCoverage {
  const months = monthsBetween(periodStart, periodEnd);
  const current = imports.filter((item) => item.license_id === licenseId);
  const cells: EvidenceCell[] = [];
  for (const month of months) {
    for (const profile of REQUIRED_PROFILES) {
      const source = current.find(
        (item) => item.profile === profile.id && item.period_start <= `${month}-01` && item.period_end >= monthEnd(month),
      ) ?? null;
      const attestation = attestations.find(
        (item) => item.license_id === licenseId && item.profile === profile.id && item.month === month,
      ) ?? null;
      cells.push({
        profile: profile.id,
        month,
        status: source ? "ready" : attestation ? "no_activity" : "missing",
        source,
        attestation,
      });
    }
  }
  const stale = current.filter((item) => item.period_end < periodStart || item.period_start > periodEnd);
  const missing = cells.filter((cell) => cell.status === "missing");
  return { months, cells, missing, stale, ready: missing.length === 0 };
}

function monthEnd(month: string): string {
  const [year, number] = month.split("-").map(Number);
  const day = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, "0")}`;
}

export type CloseStatus =
  | "not_started"
  | "evidence_missing"
  | "draft"
  | "review_saved"
  | "posting"
  | "posted"
  | "locked"
  | "failed"
  | "correction_required";

export function closeStatus(
  review: CloseReview | null,
  coverage: EvidenceCoverage,
): CloseStatus {
  if (review?.state === "correction_required") return "correction_required";
  if (review?.state === "failed") return "failed";
  if (review?.state === "locked") return "locked";
  if (review?.state === "posted") return "posted";
  if (review?.state === "posting") return "posting";
  if (review?.state === "review_saved") return "review_saved";
  if (!coverage.ready) return coverage.stale.length > 0 ? "evidence_missing" : "not_started";
  return "draft";
}

export function statusLabel(status: CloseStatus): string {
  switch (status) {
    case "not_started": return "Not started";
    case "evidence_missing": return "Evidence missing";
    case "draft": return "Review in progress";
    case "review_saved": return "Saved";
    case "posting": return "Posting";
    case "posted": return "Posted";
    case "locked": return "Locked";
    case "failed": return "Posting failed";
    case "correction_required": return "Correction required";
  }
}

export function statusTone(status: CloseStatus): "success" | "danger" | "neutral" | "warning" {
  switch (status) {
    case "posted":
    case "locked":
      return "success";
    case "failed":
    case "correction_required":
      return "danger";
    case "posting":
    case "evidence_missing":
      return "warning";
    default:
      return "neutral";
  }
}
